import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong, dateTimeString } from "../../lib/utils";

const defaultPlan = { 
    Id: 0, 
    Title: "None",
    Migrations: [],
    AppliedMigrations: []
};


const getMigrationSubscriptions = (migration) => {
    let schema = [
        {id: "statusText", dataType: 3, title: "Status"},
        {id: "party", dataType: 3, title: "Party"},
        {id: "startedOnUTC", dataType: 5, title: "Start Date (UTC)", format: "yyyy-MMM-dd hh:mm"},
        {id: "endedOnUTC", dataType: 5, title: "End Date (UTC)", format: "yyyy-MMM-dd hh:mm"},
        {id: "duration", dataType: 2, title: "Duration", thousandSeparator: ","},
        {id: "error", dataType: 3, title: "Error"},
    ];

    let data = [];

    let result = {
        schema: schema,
        data: []
       };
    

    if (migration.Status == null || migration.Status == -1)
       return result;


    let succeeded = migration.History.find(e => e.Status == 0 || e.Status == 1).Details.filter(e => e.Status == true);
    let failed = migration.History.find(e => e.Status == 0 || e.Status == 1).Details.filter(e => e.Status == false);
    
    if (failed && failed.length > 0)
    {
        for (let d of failed)
        {
            result.data.push({
                statusText: "Failure",
                party: d.SubscriptionId == null ? "Plan" : d.SubscriptionName,                
                startedOnUTC: new Date(d.StartedOnUTC),
                endedOnUTC: new Date(d.EndedOnUTC),
                duration: d.Duration,
                error: d.ErrorMessage
            })
        };
    }

    if (succeeded && succeeded.length > 0)
    {
        for (let d of succeeded)
        {
            result.data.push({
                statusText: "Success",
                party: d.SubscriptionId == null ? "Plan" : d.SubscriptionName,
                startedOnUTC: new Date(d.StartedOnUTC),
                endedOnUTC: new Date(d.EndedOnUTC),
                duration: d.Duration,
                error: ""
            })
        };
    }

    return result;
};


const getMigrationHistory = (migration) => {
    let schema = [
        {id: "statusText", dataType: 3, title: "Status"},
        {id: "party", dataType: 3, title: "Party"},
        {id: "startedOnUTC", dataType: 5, title: "Start Date (UTC)", format: "yyyy-MMM-dd hh:mm"},
        {id: "endedOnUTC", dataType: 5, title: "End Date (UTC)", format: "yyyy-MMM-dd hh:mm"},
        {id: "duration", dataType: 2, title: "Duration", thousandSeparator: ","},
        {id: "error", dataType: 3, title: "Error"},
    ];

    let data = [];

    let result = {
        schema: schema,
        data: []
       };
    

    if (migration.Status == null)
       return result;


    for (let m of migration.History)
    {
        if (m.Status == -1)
        {
            //error with no detail
            result.data.push({
                statusText: "Failure",
                party: "Migration",                
                startedOnUTC: new Date(m.StartedOnUTC),
                endedOnUTC: new Date(m.EndedOnUTC),
                duration: m.Duration,
                error: m.ErrorMessage
            });
        }
        else
        {
            let succeeded = m.Details.filter(e => e.Status == true);
            let failed = m.Details.filter(e => e.Status == false);
            
            if (failed && failed.length > 0)
            {
                for (let d of failed)
                {
                    result.data.push({
                        statusText: "Failure",
                        party: d.SubscriptionId == null ? "Plan" : d.SubscriptionName,                
                        startedOnUTC: new Date(d.StartedOnUTC),
                        endedOnUTC: new Date(d.EndedOnUTC),
                        duration: d.Duration,
                        error: d.ErrorMessage
                    })
                };
            }
        
            if (succeeded && succeeded.length > 0)
            {
                for (let d of succeeded)
                {
                    result.data.push({
                        isSuccess: true,
                        statusText: "Success",
                        party: d.SubscriptionId == null ? "Plan" : d.SubscriptionName,
                        startedOnUTC: new Date(d.StartedOnUTC),
                        endedOnUTC: new Date(d.EndedOnUTC),
                        duration: d.Duration,
                        error: ""
                    })
                };
            }
        }
    }    

    return result;
};

const defaultFormInitialValues = {
    Id: 0, 
    PlanId: 0,
    Name: "",
    Key: "",
    Version: "",
    Description: "", 
    IsActive: true,
    DeactivateReason: "",
    BasicSchemaChanged: false,
    Status: null,    
    Entities: [],
    Subscriptions: getMigrationSubscriptions({Status: null}),
    History: getMigrationHistory({Status: null}),
    TabIndex: 0
};

const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    mode: 0, 
    summaryTabIndex: 0,
    currentPlan: defaultPlan,
    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: {}, formServerErrors: [], 
    lookups: {
        plans: [],
        entities: []
    },
    panes: [ 
        {
            // size: "30%",
            size:"20%",
            min: "18%",
            max: "25%",
            collapsible: false
        },           
        {
            //min: "700px",
            //size:"60%",
            min: "75%",
            max: "82%",
            collapsible: false,
        }      
      ],
};

export const getStatusText = (status) => {
    if (status == null)
        return "Not Applied";
    else if (status == -1)
        return "Applied and Failed";
    else if (status == 0)
        return "Applied and Partially Succeeded";
    else if (status == 1)
        return "Applied and Fully Succeeded";
    
    return "";
};




const migrationsPageSlice = createSlice({
    name: "migrationsPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPanes: (state, action) => ({...state, panes: {...action.payload}}),

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),
        
        setFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                formValues: {...state.formValues, [id]: value}
            });
        },

        setFormClientErrors: (state, action) => ({...state, formClientErrors: {...action.payload}}),

        setFormServerErrors: (state, action) => ({...state, formServerErrors: [...action.payload]}),

        pageLoaded : (state, action) => {
            //payload: {Plans: includes the default one , Entities }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                mode: 0,
                summaryTabIndex: 0,
                currentPlan: defaultPlan,
                formKey: (new Date()).toString(), formValues:{...defaultFormInitialValues}, formInitialValues:{...defaultFormInitialValues}, formClientErrors:{}, formServerErrors:[],            
                lookups:{ 
                    plans: action.payload.Plans, 
                    entities: action.payload.Entities
                },
            };
        },

        planLoaded: (state, action) => {
            //payload: {plan object}
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;
                
            let loadedEntity = action.payload;

            //update the loaded plan info inside lookups            
            let newLookups = {
                ...pageState.lookups, 
                plans: [...pageState.lookups.plans.filter(e => e.Id != loadedEntity.Id), {Id: loadedEntity.Id, Title: loadedEntity.Title}]
                .sort((e1, e2) => {
                    if (e1.Title.toLowerCase() < e2.Title.toLowerCase())
                        return -1;
                    else if (e1.Title.toLowerCase() == e2.Title.toLowerCase())
                        return 0;
                    return 1;
                })
            };               

            return ({
                ...state,
                processing: false, processingText: "", 
                mode: 0, 
                summaryTabIndex: 0,
                currentPlan: {...action.payload }, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: { }, formServerErrors: [], 
                lookups: newLookups
            });
        },
      
        entityLoaded: (state, action) => {
            //action.payload: the fetched entity
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;


            let id = action.payload.Id;

            let migrationSubscriptions = getMigrationSubscriptions(action.payload);
            let migrationHistory = getMigrationHistory(action.payload);

            return {...state, 
                processing: false, processingText: "",
                mode: 2, 
                formKey: (new Date()).toString(), 
                formInitialValues: {
                    ...action.payload,
                    Subscriptions: migrationSubscriptions,
                    History: migrationHistory,
                    TabIndex: 0
                }, 
                formValues: {
                    ...action.payload,
                    Subscriptions: migrationSubscriptions,
                    History: migrationHistory,
                    TabIndex: 0
                }, 
                formClientErrors: {}, formServerErrors: [],  
                currentPlan: {
                    ...state.currentPlan,
                    Migrations: state.currentPlan.Migrations.map(e =>{
                        if (e.Id != id)
                            return e;
                        else
                            return {...action.payload}
                    }),
                    AppliedMigrations: [
                        ...state.currentPlan.AppliedMigrations.filter(e => e.MigrationId != id),
                        ...action.payload.AppliedMigrations
                    ].sort((e1, e2)=>{
                            return e1.Id - e2.Id
                        })
                },      
            };
        },         

        createEntity: (state, action) => {
            const pageState = current(state);
            const currentPlanId = pageState.currentPlan.Id;

            return ({
                ...state, 
                mode: 1, 
                formKey: (new Date()).toString(), 
                formInitialValues: {
                    ...defaultFormInitialValues,
                    PlanId: pageState.currentPlan.Id
                }, 
                formValues: {
                    ...defaultFormInitialValues,
                    PlanId: pageState.currentPlan.Id
                }, 
                formClientErrors: {}, formServerErrors: []
            });
        },

        entityInserted: (state, action) => {
            //payload: {id: ,key: , version: }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const id = action.payload.id;
            const key = action.payload.key;
            const version = action.payload.version;

            const insertedEntity = {...pageState.formValues, Id: id, Key: key, Version: version};
            
            const { Subscriptions, History, TabIndex, ...insertedEntityExclusive } = insertedEntity;

            let currentPlan = {...pageState.currentPlan};
          
            //add entity to it
            currentPlan = {...currentPlan, Migrations: [...currentPlan.Migrations, {...insertedEntityExclusive, History: [], AppliedMigrations: []}]};

            return ({
                ...state, 
                mode: 2, 
                currentPlan: currentPlan, 
                formKey: (new Date()).toString(), 
                formValues: {...insertedEntity}, 
                formInitialValues: {...insertedEntity}, 
                formClientErrors: {}, formServerErrors: []
            });
        },

        entityUpdated: (state, action) => {
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const entityId = pageState.formValues.Id;

            //rebind in list
            let currentPlan = {...pageState.currentPlan};

            //remove -> add entity to it
            currentPlan = {...currentPlan, Migrations: currentPlan.Migrations.map(item => {
                if (item.Id == entityId)
                {
                    const { Subscriptions, History, TabIndex, ...insertedEntityExclusive } = pageState.formValues;

                    return {...insertedEntityExclusive, History: item.History, AppliedMigrations: item.AppliedMigrations};
                }
                else
                    return item;
            })};

            return ({
                ...state, 
                currentPlan: currentPlan, 
                formKey: (new Date()).toString(), 
                formInitialValues: {...state.formValues}, 
                formClientErrors:{}, formServerErrors: []
            });
        },

        entityActivatedDeactivated: (state, action) => {
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const entityId = action.payload.id;
            const activate = action.payload.activate;

            //rebind in list
            let currentPlan = {...pageState.currentPlan};

            //remove -> add entity to it
            currentPlan = {...currentPlan, Migrations: currentPlan.Migrations.map(item => {
                if (item.Id == entityId)
                    return {...item, IsActive: activate}
                else
                    return item;
            })};

            return ({
                ...state, 
                currentPlan: currentPlan, 
                formKey: (new Date()).toString(), 
                formInitialValues: {
                    ...state.formValues,
                    IsActive: activate
                }, 
                formValues: {
                    ...state.formValues,
                    IsActive: activate
                }, 
                formClientErrors:{}, formServerErrors: []
            });
        },        

        removeEntityFromUI: (state, action) => {
            //payload: the entity id
            let pageState = current(state);
            let entityId = action.payload;
            let newCurrentPlan = {...pageState.currentPlan};

            //compute currentPlan, IF it contains the removed entity, by removing the entity from it            
            if (newCurrentPlan.Migrations.find(e => e.Id == entityId))
                newCurrentPlan = {...newCurrentPlan, Migrations: newCurrentPlan.Migrations.filter(e => e.Id != entityId)};
          
            //if it's the current entity => goto no mode
            let currentMode = {};

            if (pageState.mode == 2 && pageState.formValues.Id == entityId)
                currentMode = {
                    mode: 0, 
                    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: {}, formServerErrors: [] 
            };

            return ({...state, currentPlan: newCurrentPlan, ...currentMode});
        },

        entityDeleted: (state, action) => {
            //payload: id of the deleted entity
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            const deletedEntityId = action.payload;
            let isCurrentEntity = false;

            if (pageState.formValues.Id == deletedEntityId)
                isCurrentEntity = true;
            
            //remove the entity from currentPlan
            let newCurrentPlan = {
                ...pageState.currentPlan, 
                Migrations: [...pageState.currentPlan.Migrations.filter(e => e.Id != deletedEntityId)]
            };
            
            let newMode={};

            if(isCurrentEntity)
                newMode={
                    mode: 0, 
                    formKey: (new Date()).toString(), 
                    formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, 
                    formClientErrors: {}, formServerErrors: []
                };

            return ({...state, currentPlan:newCurrentPlan, ...newMode});
        },

        reflectApplyMigration: (state, action) => {
            const migration = action.payload;//the SignalR response

            if (!migration || !migration.migrationHistory)
                return state;

            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            let currentPlan = pageState.currentPlan;
            if (!currentPlan || currentPlan.Id == 0)
                return state;


            //update the plan state
            let newAppliedMigrations = [];

            if (migration.appliedMigrations && migration.appliedMigrations.length > 0)
            {
                newAppliedMigrations = migration.appliedMigrations.map(e => ({
                    Id: e.id,
                    MigrationId: e.migrationId,
                    MigrationName: e.migrationName,
                    Version: e.version,
                    SubscriptionId: e.subscriptionId,
                    SubscriptionName: e.subscriptionName,
                    EntityTypeId: e.entityTypeId,
                    EntityTypeTitle: e.entityTypeTitle,
                    EntityId: e.entityId,
                    EntityTitle: e.entityTitle,
                    StartedOnUTC: e.startedOnUTC,
                    EndedOnUTC: e.endedOnUTC,
                    Duration: e.duration,
                }))
            }


            //prepare the new migration history entry
            let newHistory = {
                Id: migration.migrationHistory.id,
                MigrationId: migration.migrationHistory.migrationId,
                MigrationName: migration.migrationHistory.migrationName,
                Version: migration.migrationHistory.version,
                StartedOnUTC: migration.migrationHistory.startedOnUTC,
                EndedOnUTC: migration.migrationHistory.endedOnUTC,
                Duration: migration.migrationHistory.duration,
                Stage: migration.migrationHistory.stage,
                Status: migration.migrationHistory.status,
                ErrorMessage: migration.migrationHistory.errorMessage,
                Details: migration.migrationHistory.details.map(d => ({
                    Id: d.id,
                    SubscriptionId: d.subscriptionId,
                    SubscriptionName: d.subscriptionName,
                    StartedOnUTC: d.startedOnUTC,
                    EndedOnUTC: d.endedOnUTC,
                    Duration: d.duration,
                    IsReapplied: d.isReapplied,
                    Status: d.status,
                    ErrorMessage: d.errorMessage
                }))
            };

            let newCurrentPlan = {
                ...currentPlan, 
                Migrations: currentPlan.Migrations.map(e => {
                    if (e.Id != migration.migrationId)
                       return e;

                    return {
                        ...e, 
                        Status: migration.migrationHistory.status,
                        History: [...e.History, newHistory],
                        AppliedMigrations: [...e.AppliedMigrations, ...newAppliedMigrations]
                    };
                }),
                AppliedMigrations: [...currentPlan.AppliedMigrations, ...newAppliedMigrations]
            };

            //update formValues if the entity is the one in edit mode
            let newFormValues = {...pageState.formValues};

            if (pageState.formValues && pageState.formValues.Id == migration.migrationId)
            {
                //create a deep clone of the current migration with the new migrations and applied migrations
                let newMigration = structuredClone(currentPlan.Migrations.find(e => e.Id == migration.migrationId));

                newMigration.Status = migration.status;
                newMigration.History.push({...newHistory});
                newMigration.AppliedMigrations.push(...newAppliedMigrations);

                
                newFormValues.Status = migration.status;
                newFormValues.History = getMigrationHistory(newMigration);
                newFormValues.Subscriptions = getMigrationSubscriptions(newMigration);
            };

            return {...state,
                currentPlan: newCurrentPlan,
                formValues: newFormValues
            };
        },

        reflectReapplyMigration: (state, action) => {
            const migration = action.payload;//the SignalR response

            if (!migration || !migration.migrationHistory)
                return state;


            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;


            let currentPlan = pageState.currentPlan;
            if (!currentPlan || currentPlan.Id == 0)
                return state;


            //update the plan state
            let newAppliedMigrations = [];

            if (migration.appliedMigrations && migration.appliedMigrations.length > 0)
            {
                newAppliedMigrations = migration.appliedMigrations.map(e => ({
                    Id: e.id,
                    MigrationId: e.migrationId,
                    MigrationName: e.migrationName,
                    Version: e.version,
                    SubscriptionId: e.subscriptionId,
                    SubscriptionName: e.subscriptionName,
                    EntityTypeId: e.entityTypeId,
                    EntityTypeTitle: e.entityTypeTitle,
                    EntityId: e.entityId,
                    EntityTitle: e.entityTitle,
                    StartedOnUTC: e.startedOnUTC,
                    EndedOnUTC: e.endedOnUTC,
                    Duration: e.duration,
                }))
            }


            //prepare the new migration history entry
            let detailsIds = migration.migrationHistory.details ? migration.migrationHistory.details.map(e => e.id) : [];

            //get the current migration history from currentPlan
            let migrationHistory = currentPlan.Migrations.find(e => e.Id == migration.migrationId).History.find(e => e.Id == migration.migrationHistory.id);

            migrationHistory = structuredClone(migrationHistory);

            migrationHistory.Status = migration.migrationHistory.status;
            migrationHistory.Details = migrationHistory.Details.map(e => {
                if (detailsIds.indexOf(e.Id) < 0)
                    return e;
                else
                {
                    let detail = {...migration.migrationHistory.details.find(d => d.id == e.Id)};

                    if (!detail)//will not happen
                        return e;
 
                    return {
                        Id: detail.id,
                        SubscriptionId: detail.subscriptionId,
                        SubscriptionName: detail.subscriptionName,
                        StartedOnUTC: detail.startedOnUTC,
                        EndedOnUTC: detail.endedOnUTC,
                        Duration: detail.duration,
                        IsReapplied: detail.isReapplied,
                        Status: detail.status,
                        ErrorMessage: detail.errorMessage
                    };
                }                    
            });


            let newCurrentPlan = {
                ...currentPlan, 
                Migrations: currentPlan.Migrations.map(e => {
                    if (e.Id != migration.migrationId)
                       return e;
                    else
                        return {
                            ...e, 
                            Status: migration.status,
                            History: e.History.map(h => {
                                if (h.Id != migration.migrationHistory.id)
                                    return h;
                                else
                                    return migrationHistory;
                            }),
                            AppliedMigrations: [...e.AppliedMigrations, ...newAppliedMigrations]
                        };
                }),
                AppliedMigrations: [...currentPlan.AppliedMigrations, ...newAppliedMigrations]
            };


            //update formValues if the entity is the one in edit mode
            let newFormValues = {...pageState.formValues};

            if (pageState.formValues && pageState.formValues.Id == migration.migrationId)
            {
                //create a deep clone of the current migration with the new migrations and applied migrations
                let newMigration = structuredClone(currentPlan.Migrations.find(e => e.Id == migration.migrationId));

                newMigration.Status = migration.status;

                newMigration.History = newMigration.History.map(h => {
                    if (h.Id != migration.migrationHistory.id)
                       return h;
                    else
                       return structuredClone(migrationHistory);
                });       

                newMigration.AppliedMigrations.push(...newAppliedMigrations);
              
                newFormValues.Status = migration.status;
                newFormValues.History = getMigrationHistory(newMigration);
                newFormValues.Subscriptions = getMigrationSubscriptions(newMigration);
            };


            return {...state,
                currentPlan: newCurrentPlan,
                formValues: newFormValues
            };
        },

        gotoGlobalView: (state, action) => {
            return {
                ...state, 
                mode: 0, 
                formKey: (new Date()).toString(), 
                formValues: {...defaultFormInitialValues}, 
                formInitialValues: {...defaultFormInitialValues}, 
                formClientErrors: {}, formServerErrors: []
            }
        },

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default migrationsPageSlice.reducer;


export const loadPage = (t) => async (dispatch, getState) => {
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'migration/getmigrationspagedata',
                method:  'post',
                data: {
                },
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
            return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
        //add the 0/None plan
        data.Plans.splice(0, 0, {...defaultPlan});
       
        dispatch(pageLoaded(data));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadPlan = (planId, t) => async (dispatch, getState) => {
    //show loading
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPlan")}));

    if (planId == 0)
    {   
        dispatch(planLoaded({...defaultPlan}));
        return;
    }

    try{
        var response = await api({            
            url: "migration/getplanbyid",
            method:  'post',
            data: {
                id: planId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(planLoaded(data));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const loadEntity = (entityId, t) => async (dispatch, getState) => {
    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingMigration")}));

    try{
        var response = await api({            
            url: "migration/getmigrationbyid",
            method:  'post',
            data: {
                id: entityId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

        let data = response.data.Data;

        //transform data
        dispatch(entityLoaded(data));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const saveEntity = (t) => async (dispatch, getState) => {
    const pageState = getState().migrationsPage;

    let entityId = pageState.formValues.Id;
    let url = pageState.mode == 1 ? "migration/createmigration" : "migration/changemigrationinfo";
    let title = pageState.mode == 1 ? t("slices.creatingMigration") : t("slices.updatingMigration");

    //show loader
    dispatch(setProcessing({processing: true, processingText: title}));

    const { Subscriptions, History, TabIndex, ...exclusiveData } = pageState.formValues;

    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {...exclusiveData},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

        let data = response.data.Data;

        if (pageState.mode == 1)
            dispatch(entityInserted({id: data.Id, key: data.Key, version: data.Version}));
        else
            dispatch(entityUpdated());
        
        let successMessage = pageState.mode == 1 ? t("slices.createdMigration") : t("slices.updatedMigration");

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const activateDeactivateEntity = (activate, t) => async (dispatch, getState) => {
    const pageState = getState().migrationsPage;

    let entity = pageState.formValues;
    let entityId = entity.Id;
    let url = activate ? "migration/activatemigration" : "migration/deactivatemigration";

    //show loader
    dispatch(setProcessing({processing: true, processingText: activate ? t("slices.activatingMigration") : t("slices.deactivatingMigration")}));

    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {
                id: entityId,
                deactivateReason: ""
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

        let data = response.data.Data;      
        
        let successMessage = activate ? t("slices.activatedMigration") : t("slices.deactivatedMigration");

        dispatch(entityActivatedDeactivated({id: entityId, activate: activate}));

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
    //payload the entity id
    dispatch(setProcessing({processing: true, processingText: t("slices.deletingMigration")}));

    try{
        var response = await api({            
            url:"/migration/removemigration",
            method:  'post',
            data: {id: id},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(addNotification({text: t("slices.deletedMigration"), type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        dispatch(entityDeleted(id));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};


export const applyMigration = (t) => async (dispatch, getState) => {
    let entityId = getState().migrationsPage.formValues.Id;

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.applyingMigration")}));

    let signalRConnectionId = getState().signalR.connectionId;

    try{
        var response = await api({            
            url: "migration/applymigration",
            method:  'post',
            data: {
                id: entityId,
                connectionId: signalRConnectionId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

           dispatch(addNotification({text: t("slices.startedMigration"), type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

           dispatch(setProcessing({processing: false}));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const applyMigrationTerminated = (response, t) => async(dispatch, getState) => {
    let errorMessage = "";

    if (response.exception)
    {
        dispatch(addNotification({text: t("slices.migration") + (response.migrationName ? response.migrationName : "") + t("slices.failedMigration") + response.exception , type: "error", closable: true, duration:notificationDurationLong, position:"topRight"}));
        return;
    }

    if (response.status == -1)
    {
        //complete failure
        if (response.migrationHistory && response.migrationHistory.errorMessage)    
            errorMessage = response.migrationHistory.errorMessage;

        dispatch(addNotification({text: t("slices.migration") + (response.migrationName ? response.migrationName : "") + t("slices.failed") + response.migrationHistory.errorMessage, type: "error", closable: true, duration:notificationDurationLong, position:"topRight"}));

        dispatch(reflectApplyMigration(response));    
    }
    else if (response.status == 0)
    {
        dispatch(addNotification({text: t("slices.migration") + (response.migrationName ? response.migrationName : "") + t("slices.successMigration"), type: "warning", closable: true, duration:notificationDurationLong, position:"topRight"}));
        
        dispatch(reflectApplyMigration(response));    
    }
    else if (response.status == 1)
    {
        dispatch(addNotification({text: t("slices.migration") + (response.migrationName ? response.migrationName : "") + t("slices.success"), type: "success", closable: true, duration:notificationDurationLong, position:"topRight"}));
        
        dispatch(reflectApplyMigration(response)); 
    }
};


export const reapplyMigration = (t) => async (dispatch, getState) => {
    let entityId = getState().migrationsPage.formValues.Id;
    
    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.reapplyingMigration")}));

    let signalRConnectionId = getState().signalR.connectionId;

    try{
        var response = await api({            
            url: "migration/reapplymigration",
            method:  'post',
            data: {
                id: entityId,
                connectionId: signalRConnectionId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().migrationsPage.pageDisposed)
           return;

           dispatch(addNotification({text: t("slices.restartedMigration"), type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

           dispatch(setProcessing({processing: false}));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const reapplyMigrationTerminated = (response, t) => async(dispatch, getState) => {
    let errorMessage = "";

    if (response.exception)
    {
        dispatch(addNotification({text: t("slices.reapplictionMigration") + (response.migrationName ? response.migrationName : "") + t("slices.reappfailedMigration") + response.exception , type: "error", closable: true, duration:notificationDurationLong, position:"topRight"}));
        return;
    }

    if (response.status == 0)
    {
        let res = t("slices.failed1");
        if (response.migrationHistory && response.migrationHistory.details && response.migrationHistory.details.find(e => e.status == true))
            res = t("slices.partialSuccess");

        dispatch(addNotification({text: t("slices.reapplictionMigration") + (response.migrationName ? response.migrationName : "") + " " + res + t("slices.checkMigration"), type: "warning", closable: true, duration:notificationDurationLong, position:"topRight"}));
        
        dispatch(reflectReapplyMigration(response));    
    }
    else if (response.status == 1)
    {
        dispatch(addNotification({text: t("slices.reapplictionMigration") + (response.migrationName ? response.migrationName : "") + t("slices.success"), type: "success", closable: true, duration:notificationDurationLong, position:"topRight"}));
        
        dispatch(reflectReapplyMigration(response)); 
    }
};

export const { setInitialState, setProcessing, setPageValue, pageLoaded, setPanes, planLoaded, createEntity, setFormValue, setFormClientErrors, setFormServerErrors, entityLoaded, removeEntityFromUI, entityInserted, entityUpdated, entityActivatedDeactivated, entityDeleted, reflectApplyMigration, reflectReapplyMigration, gotoGlobalView, disposePage } = migrationsPageSlice.actions;

